/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ErrorMessage,
  MultiSelect,
  SelectDropdown,
  TextInput,
  RichTextEditor,
} from '@gsa/afp-component-library';
import { isEmpty } from 'lodash';
import EquipmentCodeTypeAhead from './equipment-code-typeahead';
import {
  getRules,
  renderCodeError,
  renderSequenceError,
  renderTitleError,
  validate,
} from '../../utilities/validations/error-rules';
import InteractiveCheck from '../InteractiveCheck/InteractiveCheck';
import { emDashUnicode } from '../../utilities/constants';

const EquipmentCodeForm = ({
  formId,
  handleSave = () => null,
  isAddForm = false,
  yearOptions,
  categoryOptions,
  quantityRequiredOptions,
  unitOptions,
  tagOptions,
  programs,
  isReadOnly = false,
  isPartialEditable = false, // the equipment code year is = current contract year
  isEqInContractYear = false,
}) => {
  const {
    register,
    watch,
    formState: { errors },
    control,
    setValue,
    handleSubmit,
    clearErrors,
  } = useFormContext();

  const watchContent = watch('content');
  const watchEqYear = !isAddForm ? watch('year') : null;
  const selectedYear = watch('year');

  const onSubmit = (data) => {
    handleSave({ ...data, content: watchContent, copyFromYear: +selectedYear });
  };

  const statusEnum = {
    Active: 'Active',
    Inactive: 'Inactive',
  };

  const statusesOptions = [
    { value: '', label: 'Select' },
    { value: statusEnum.Active, label: 'Active' },
    { value: statusEnum.Inactive, label: 'Inactive' },
  ];

  const equipmentCodeCreationYearOptions = yearOptions?.filter((year) => {
    return year?.value >= new Date().getFullYear().toString();
  });

  const currentYear = new Date().getFullYear();
  const isPrevFvsYear = watchEqYear === currentYear;
  const isCurrentFvsYear = watchEqYear === currentYear + 1;

  // code
  const isDirty = !isEmpty(watch('code')) || errors?.code?.message;
  const rules = getRules(watch('code'), 'EquipmentCode');
  const hasCodeError = errors?.code?.message;

  // Catalog Title
  const isTitleDirty = !isEmpty(watch('title')) || errors?.title?.message;
  const titleRules = getRules(watch('title'), 'Title');
  const isTitleValid = validate(titleRules);
  const hasTittleError = errors?.title?.message;
  const canShowTitleError = !isTitleValid && isTitleDirty && hasTittleError;

  // Sequence
  const isSequenceDirty =
    !isEmpty(watch('sequence')) || errors?.sequence?.message;
  const Sequencerules = getRules(watch('sequence'), 'Sequence');
  const isSequenceValid = validate(Sequencerules);
  const hasSequenceError = !isSequenceValid && isSequenceDirty;

  // if form is Edit and isReadOnly is true then disable the inputs
  const disabled = !isAddForm && isReadOnly;
  // Edit form and partial editable
  const partialEditable = !isAddForm && isPartialEditable;

  // tags that can't be edited in the current year
  const noneEditableTags = new Set(['MPG']);

  const eQTagOptions = tagOptions.map((tag) => ({
    ...tag,
    disabled:
      noneEditableTags.has(tag.value) &&
      (partialEditable || isEqInContractYear),
  }));

  const isStatusEditable = (currentStatus) => {
    // if the equipment code is partially editable(current calendar year) and current status is inactive, then the status is editable
    if (partialEditable && currentStatus === statusEnum.Active && !disabled)
      return true;
    // if the equipment code is in awarded contract and the current status is Inactive, then the status is editable
    if (isEqInContractYear && currentStatus === statusEnum.Active && !disabled)
      return true;
    return false;
  };

  // Renders the red red asterisk next the h2.
  const renderRequiredComponent = (value) => {
    return (
      <h2 className="font-sans-xs" aria-label={value}>
        {value} <span className="usa-hint usa-hint--required">*</span>
      </h2>
    );
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      {/* Program */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {renderRequiredComponent('Program')}
          <Controller
            data-testid="eq_edit_program"
            name="program"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                data-testid="eq_edit_program_sel"
                aria-label="program"
                aria-required="true"
                {...field}
                options={
                  programs
                    ? [{ label: '- Select -', value: '' }, ...programs]
                    : []
                }
                disabled={isReadOnly || isPrevFvsYear || isCurrentFvsYear}
              />
            )}
          />
          {errors?.program?.message && (
            <ErrorMessage>{errors.program.message}</ErrorMessage>
          )}
        </div>
      </div>
      {/* Code */}
      <div
        className={`grid-row padding-bottom-2 ${
          hasCodeError ? 'padding-left-2' : null
        }`}
      >
        <div className="grid-col-10">
          {isAddForm && (
            <>
              {renderRequiredComponent('Code')}
              <Controller
                data-testid="code-controller"
                name="code"
                control={control}
                render={({ field }) => (
                  <TextInput
                    data-testid="code"
                    aria-label="code"
                    aria-required="true"
                    {...field}
                    type="text"
                    errorMessage={renderCodeError(
                      hasCodeError,
                      'Make sure the code meets the requirements listed below.',
                    )}
                  />
                )}
              />
            </>
          )}
          {!isAddForm && (
            <>
              <h2 className="font-sans-xs" aria-label="Code">
                Code
              </h2>
              <span className=".text-normal">{watch('code')}</span>
            </>
          )}
          {hasCodeError && (
            <InteractiveCheck checks={rules} isDirty={isDirty} />
          )}
        </div>
      </div>

      {/* Title */}
      <div
        className={`grid-row padding-bottom-2 ${
          hasTittleError ? 'padding-left-2' : null
        }`}
      >
        <div className="grid-col-10">
          {renderRequiredComponent('Title')}
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextInput
                data-testid="eq_edit_title"
                aria-label="title"
                aria-required="true"
                {...field}
                type="text"
                errorMessage={renderTitleError(canShowTitleError)}
              />
            )}
          />
          {hasTittleError && (
            <InteractiveCheck checks={titleRules} isDirty={isTitleDirty} />
          )}
        </div>
      </div>

      {/* Contract Year */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {isAddForm ? (
            <>
              {renderRequiredComponent('Contract year')}
              <Controller
                data-testid="eq_startYear"
                name="year"
                control={control}
                defaultValue={(new Date().getFullYear() + 1).toString()}
                render={({ field }) => (
                  <SelectDropdown
                    data-testid="eq_startYear_sel"
                    aria-label="Contract year"
                    aria-required="true"
                    {...field}
                    value={field.value}
                    options={[...equipmentCodeCreationYearOptions]}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
              {errors?.year?.message && (
                <ErrorMessage>{errors.year.message}</ErrorMessage>
              )}
            </>
          ) : (
            <>
              <h2 className="font-sans-xs">Contract year</h2>
              <span className=".text-normal">{watch('year')}</span>
            </>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {renderRequiredComponent('Status')}
          <Controller
            data-testid="eq_edit_status"
            name="status"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                data-testid="eq_edit_status_sel"
                aria-label="status"
                aria-required="true"
                {...field}
                options={statusesOptions}
                disabled={isStatusEditable(field.value)}
              />
            )}
          />
          {errors?.status?.message && (
            <ErrorMessage>{errors.status.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Category */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {renderRequiredComponent('Category Code')}
          <Controller
            data-testid="eq_category"
            name="category"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                data-testid="eq_category_sel"
                aria-label="category"
                aria-required="true"
                {...field}
                options={[
                  { label: '- Select -', value: '' },
                  ...categoryOptions,
                ]}
                disabled={disabled}
              />
            )}
          />
          {errors?.category?.message && (
            <ErrorMessage>{errors.category.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Sequence */}
      <div
        className={`grid-row padding-bottom-2 ${
          hasSequenceError ? 'padding-left-2' : null
        }`}
      >
        <div className="grid-col-10">
          {renderRequiredComponent('Sequence')}
          <Controller
            data-testid="sequence"
            name="sequence"
            control={control}
            render={({ field }) => (
              <TextInput
                data-testid="eq_edit_sequence"
                aria-label="sequence"
                aria-required="true"
                {...field}
                type="text"
                errorMessage={renderSequenceError(
                  hasSequenceError,
                  'Make sure the Sequence meets the requirements listed below.',
                )}
              />
            )}
          />
          {hasSequenceError && (
            <InteractiveCheck
              checks={Sequencerules}
              isDirty={isSequenceDirty}
            />
          )}
        </div>
      </div>

      {/* Valid Quantity */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {renderRequiredComponent('Valid quantity')}
          <Controller
            data-testid="eq_edit_quantityRequiredCode"
            name="quantityRequired"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                data-testid="eq_edit_quantityRequiredCode_sel"
                aria-label="Valid quantity required"
                {...field}
                options={quantityRequiredOptions}
                disabled={disabled || isPrevFvsYear || isEqInContractYear}
              />
            )}
          />
          {errors?.quantityRequired?.message && (
            <ErrorMessage>{errors.quantityRequired.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Unit */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <h2 className="font-sans-xs">Unit</h2>
          <Controller
            data-testid="eq_edit_unit"
            name="unit"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                data-testid="eq_edit_unit_sel"
                aria-label="unit"
                {...field}
                options={unitOptions}
                disabled={disabled || isPrevFvsYear || isEqInContractYear}
              />
            )}
          />
          {errors?.unitCode?.message && (
            <ErrorMessage>{errors.unitCode.message}</ErrorMessage>
          )}
        </div>
      </div>

      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <h2 className="font-sans-xs">Tags</h2>
          {disabled ? (
            <span className=".text-normal">
              {watch('tags')
                ?.map((tag) => tag)
                .join(', ') || emDashUnicode}
            </span>
          ) : (
            <>
              <Controller
                id="eq_form_tags"
                name="tags"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    data-testid="si_form_tags_sel"
                    key="eq_form_tags_sel"
                    name="tags"
                    options={eQTagOptions}
                    value={field.value}
                    onChange={(val, checked) => {
                      let changedValue;
                      if (checked) {
                        changedValue = [...field.value, val];
                      } else {
                        changedValue = field.value.filter((el) => el !== val);
                      }
                      field.onChange(changedValue);
                    }}
                    selectedValues={field.value}
                  />
                )}
              />
              {errors?.tags?.message && (
                <ErrorMessage>{errors.tags.message}</ErrorMessage>
              )}
            </>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-auto">
          <h2 className="font-sans-xs"> Description </h2>
        </div>
        <Controller
          data-testid="eq_edit_description"
          name="content"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              data-testid="eq_edit_description"
              label="Description"
              aria-label="description"
              onChange={field.onChange}
              content={field.value || ''}
            />
          )}
        />
        {errors?.content?.message && (
          <ErrorMessage>{errors.content.message}</ErrorMessage>
        )}
      </div>

      {isAddForm && (
        <div className="grid-row padding-bottom-1">
          <div className="grid-col-4">
            <h2
              className="font-sans-xs text-bold text-primary"
              style={{
                fontSize: '16px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              Copy Standard Items From
            </h2>
          </div>
        </div>
      )}

      {isAddForm && (
        <div className="grid-row padding-bottom-2">
          <div className="grid-col-6">
            <h2 className="font-sans-xs">Copy from Contract year</h2>
            <Controller
              data-testid="eq_copyFromYear"
              name="copyFromYear"
              control={control}
              render={({ field }) => (
                <TextInput
                  data-testid="eq_copyFromYear_txt"
                  aria-label="Copy from Contract year"
                  {...field}
                  value={selectedYear}
                  type="text"
                  disabled={true}
                />
              )}
            />
            {errors?.copyFromYear?.message && (
              <ErrorMessage>{errors.copyFromYear.message}</ErrorMessage>
            )}
          </div>
        </div>
      )}

      <EquipmentCodeTypeAhead
        isNew={isAddForm}
        control={control}
        errors={errors}
        setValue={setValue}
        register={register}
        watch={watch}
        year={selectedYear}
        program={watch('program')}
        nameValue="copyFromEquipmentCodeId"
        clearErrors={clearErrors}
      />
    </form>
  );
};

EquipmentCodeForm.propTypes = {
  formId: PropTypes.string.isRequired,
  handleSave: PropTypes.func,
  isAddForm: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isPartialEditable: PropTypes.bool,
};

export default EquipmentCodeForm;
